/**
 * Shared UI role suffixes used across the component library for data-ui-id attributes.
 *
 * These are common roles that may appear on multiple component types.
 * Components should import from here for any shared role, and define their
 * own component-specific roles locally when needed.
 *
 * Usage:
 *   import { UI_ROLES } from "@/constants/uiRoles";
 *
 *   data-ui-id={`${uiId}-${UI_ROLES.TRIGGER}`}
 */
export const UI_ROLES = {
  TRIGGER: "trigger",
  SEARCH: "search",
  RESET: "reset",
  APPLY: "apply",
  OPTION: "option",
  SELECT_ALL: "select-all",
} as const;

export type UiRole = (typeof UI_ROLES)[keyof typeof UI_ROLES];

/**
 * Role suffixes for the SideNav component.
 *
 * SideNav is a singleton on every page, so identifiers are fixed
 * (no consumer-provided uiId prefix required).
 *
 * Playwright examples:
 *   page.locator('[data-ui-id="side-nav"]')
 *   page.locator('[data-ui-id="side-nav-item-equity"]')
 *   page.locator('[data-ui-id="side-nav-pin"]')
 *   page.locator('[data-ui-id="side-nav-admin"]')
 *   page.locator('[data-ui-id="side-nav-submenu"]')
 */
export const SIDE_NAV_UI_ROLES = {
  /** Outermost sidebar wrapper */
  CONTAINER: "side-nav",
  /** Individual nav icon button – append `-{route-id}` */
  ITEM: "side-nav-item",
  /** Pin / unpin toggle button */
  PIN: "side-nav-pin",
  /** Admin settings icon */
  ADMIN: "side-nav-admin",
  /** Expanded sub-menu panel */
  SUBMENU: "side-nav-submenu",
} as const;

export type SideNavUiRole = (typeof SIDE_NAV_UI_ROLES)[keyof typeof SIDE_NAV_UI_ROLES];

/**
 * Role suffixes for the SideNavSubRoute component (expanded sub-menu content).
 *
 * Playwright examples:
 *   page.locator('[data-ui-id="side-nav-sub-core-accordion"]')
 *   page.locator('[data-ui-id="side-nav-sub-item-equity-brand"]')
 *   page.locator('[data-ui-id="side-nav-sub-recent-views-accordion"]')
 *   page.locator('[data-ui-id="side-nav-sub-recent-view-v1"]')
 *   page.locator('[data-ui-id="side-nav-sub-signout"]')
 */
export const SIDE_NAV_SUB_ROUTE_UI_ROLES = {
  /** "Core" accordion toggle header */
  CORE_ACCORDION: "side-nav-sub-core-accordion",
  /** Individual child route item – append `-{route-id}` */
  ITEM: "side-nav-sub-item",
  /** "Recent Views" accordion toggle header */
  RECENT_VIEWS_ACCORDION: "side-nav-sub-recent-views-accordion",
  /** Individual recent-view row – append `-{viewId}` */
  RECENT_VIEW: "side-nav-sub-recent-view",
  /** Sign-out button */
  SIGNOUT: "side-nav-sub-signout",
} as const;

export type SideNavSubRouteUiRole =
  (typeof SIDE_NAV_SUB_ROUTE_UI_ROLES)[keyof typeof SIDE_NAV_SUB_ROUTE_UI_ROLES];
